import React from 'react';
import moment from 'moment';

const display = (value) =>
  value === null || value === undefined || value === '' ? '—' : value;

const displayDate = (value) => {
  if (!value) {
    return '—';
  }
  const date = moment(value);
  return date.isValid() ? date.format('DD MMMM YYYY') : display(value);
};

const isTorinoCenter = (item) => {
  const title = item?.shopping_center?.title?.toLowerCase() || '';
  const name = item?.shopping_center?.name?.toLowerCase() || '';
  return title.includes('torino') || name.includes('torino');
};

const getSectionTitle = (sectionKey, t) => {
  const sectionTitles = {
    hero_section: t('Sezione Hero'),
    content_section: t('Sezione Contenuto'),
    items_section: t('Sezione Elementi'),
    vip_club_section: t('Sezione VIP Club'),
    instruction_section: t('Sezione Istruzioni'),
    find_us_section: t('Sezione Trovaci'),
    services_list_section: t('Sezione Lista Servizi'),
    our_services_section: t('Sezione I Nostri Servizi'),
    tourist_information_section: t('Sezione Informazioni Turistiche'),
    download_app_section: t('Sezione Download App'),
    metadata_section: t('Sezione Metadati'),
    meta: t('Sezione Metadati'),
    hours_section: t('Sezione Orari'),
    explore_section: t('Sezione Esplora'),
    transport_section: t('Sezione Trasporto'),
    torino_section: t('Sezione Torino'),
    tourism_map_section: t('Sezione Mappa Turistica'),
    events_list_section: t('Sezione Lista Eventi'),
    shops_section: t('Sezione Negozi'),
    hotels_section: t('Sezione Hotel'),
    shopping_section: t('Sezione Shopping'),
    gift_card_section: t('Sezione Gift Card'),
    offers_list_section: t('Sezione Lista Offerte'),
    brands_list_section: t('Sezione Lista Marchi'),
    find_your_brand_section: t('Sezione Trova il tuo Marchio'),
    information_section: t('Sezione Informazioni'),
    favourites_section: t('Sezione Preferiti'),
    restaurants_list_section: t('Sezione Lista Ristoranti'),
    form_section: t('Sezione Form'),
    tourist_itineraries_section: t('Sezione Itinerari Turistici'),
    partnerships_section: t('Sezione Partnership'),
  };

  return sectionTitles[sectionKey] || sectionKey;
};

const isImageUrl = (value) =>
  typeof value === 'string' &&
  (value.startsWith('http') || value.startsWith('/')) &&
  /\.(png|jpe?g|gif|svg|webp|avif)(\?|$)/i.test(value);

const formatFieldValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'string' && isImageUrl(value)) {
    return (
      <a href={value} target='_blank' rel='noreferrer'>
        {value}
      </a>
    );
  }
  if (typeof value === 'object') {
    return null;
  }
  return String(value);
};

const flattenSectionData = (data, prefix = '', fields = []) => {
  if (!data || typeof data !== 'object') {
    return fields;
  }

  Object.entries(data).forEach(([key, value]) => {
    const label = prefix ? `${prefix} → ${key}` : key;

    if (value === null || value === undefined || value === '') {
      return;
    }

    if (Array.isArray(value)) {
      if (!value.length) {
        return;
      }
      value.forEach((item, index) => {
        if (item && typeof item === 'object') {
          flattenSectionData(item, `${label} [${index + 1}]`, fields);
        } else {
          fields.push({
            text: `${label} [${index + 1}]`,
            value: formatFieldValue(item),
          });
        }
      });
      return;
    }

    if (typeof value === 'object') {
      flattenSectionData(value, label, fields);
      return;
    }

    const formatted = formatFieldValue(value);
    if (formatted !== null) {
      fields.push({ text: label, value: formatted });
    }
  });

  return fields;
};

const normalizeContent = (content) => {
  if (!content || typeof content !== 'object') {
    return { it: {} };
  }

  const firstKey = Object.keys(content)[0];
  if (['it', 'en', 'fr'].includes(firstKey)) {
    return content;
  }

  return { it: content, en: { ...content } };
};

const getLanguages = (item) => {
  const langs = ['IT', 'EN'];
  if (isTorinoCenter(item)) {
    langs.push('FR');
  }
  return langs;
};

const buildLanguageContentSections = (t, item, langKey) => {
  const content = normalizeContent(item.content);
  const langContent = content[langKey.toLowerCase()] || {};
  const sections = [];
  const sectionKeys = Object.keys(langContent).filter(
    (key) => key !== 'metadata_section' && key !== 'links_section',
  );

  if (langContent.meta) {
    sections.push({
      title: `${getSectionTitle('meta', t)} (${langKey})`,
      fields: [
        { text: t('Meta Title'), value: display(langContent.meta.title) },
        {
          text: t('Meta Description'),
          value: display(langContent.meta.description),
        },
        { text: t('Canonical URL'), value: display(langContent.meta.url) },
      ],
    });
  }

  sectionKeys.forEach((sectionKey) => {
    if (sectionKey === 'meta') {
      return;
    }

    const sectionData = langContent[sectionKey];
    const fields = flattenSectionData(sectionData);

    if (fields.length) {
      sections.push({
        title: `${getSectionTitle(sectionKey, t)} (${langKey})`,
        fields,
      });
    }
  });

  const metadataSection = langContent.metadata_section;
  if (metadataSection) {
    const metaFields =
      metadataSection.meta != null
        ? [
            {
              text: t('Meta Title'),
              value: display(metadataSection.meta?.title),
            },
            {
              text: t('Meta Description'),
              value: display(metadataSection.meta?.description),
            },
            {
              text: t('Canonical URL'),
              value: display(metadataSection.meta?.url),
            },
          ]
        : flattenSectionData(metadataSection);

    if (metaFields.length) {
      sections.push({
        title: `${getSectionTitle('metadata_section', t)} (${langKey})`,
        fields: metaFields,
      });
    }
  }

  return sections;
};

export const getStaticPageViewSections = (t, item) => {
  if (!item?.id) {
    return [];
  }

  const sections = [
    {
      title: t('Informazioni Generali'),
      fields: [
        { text: t('Titolo'), value: display(item.title) },
        { text: t('Slug'), value: display(item.slug) },
        {
          text: t('Outlet Village'),
          value: display(item.shopping_center?.title),
        },
        {
          text: t('Data creazione'),
          value: displayDate(item.created_at),
        },
      ],
    },
  ];

  getLanguages(item).forEach((lang) => {
    sections.push(...buildLanguageContentSections(t, item, lang));
  });

  return sections.filter((section) => section.fields?.length);
};
